import manualJsIcon from 'assets/images/integrations/manual-js.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Manual JavaScript — paste the universal pixel `<script>` into the site's `<head>`.
 * Trigger-based conversion rules (page_load, url_match, click, form_submit) fire
 * automatically once the pixel is loaded. For events the trigger system can't see
 * (post-checkout confirmation pages, JS-fired SPA events, custom timing), the page
 * can explicitly fire via the legacy {@code CMPIXEL.fire(pixelCode, dataObject)} API
 * exposed on the window by pixel.js.
 */
export const manualJsIntegration: PixelIntegration = {
  id: 'manual-js',
  label: 'Manual JS',
  description:
    'Paste the universal pixel into your site\'s <head>. Trigger-based rules fire automatically; use the snippet below to fire programmatically from JS.',
  icon: <img src={manualJsIcon} alt="" className="h-4 w-4" />,
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Add this snippet to every page (typically in <head>)">
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>
      <Step number={2} title="Trigger-based rules fire automatically">
        <p className="text-base-xs text-primary-gray-500">
          Once the pixel loads, it watches for the triggers you configured (page_load, url_match,
          click, form_submit) and fires the matching conversion in the browser. No extra code needed
          on the page.
        </p>
      </Step>
      <Step number={3} title="Need to fire manually? Use CMPIXEL.fire()">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          For pages where the trigger system can&apos;t reach (single-page-app route changes,
          post-submit confirmation screens, custom JS timing), call{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            CMPIXEL.fire(pixelCode, dataObject)
          </code>{' '}
          directly. Each rule below has a ready-to-paste call.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildCmpixelFireSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

/**
 * Mirror the legacy {@code CMPIXEL.fire} format used by ctv-platform-fe's PixelCode/
 * ExampleCategory.tsx so existing client integrations don't have to learn a new API. Includes
 * extractor placeholders for checkout-style rules (amount / currency / order_number).
 */
function buildCmpixelFireSnippet(
  rule: ConversionRule,
  ctx: IntegrationContext,
): IntegrationSnippet {
  const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '\'<your order total>\''
        : key === 'currency'
          ? '\'USD\''
          : key === 'order_number'
            ? '\'<your order id>\''
            : '\'\'';

    lines.push(`  '${key}': ${placeholder}`);
  }
  const body = lines.join(',\n');

  const code = [
    `<script src="${ctx.cdnUrl}"></script>`,
    '<script>',
    `CMPIXEL.fire('${ctx.pixelCode}', {`,
    body,
    '});',
    '</script>',
  ].join('\n');

  return {
    code,
    language: 'html',
    label: 'CMPIXEL.fire() — paste anywhere on the page',
    note: 'If you already have the universal pixel in <head>, you can drop the first <script src> line and use just the inline call.',
  };
}
